import { Component, ErrorHandler, OnInit, ChangeDetectionStrategy, signal } from '@angular/core'
import { form, maxLength } from '@angular/forms/signals'
import { MatDialog } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import { firstValueFrom } from 'rxjs'

import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjanpitajaService } from 'app/_angular/service/kirjanpitaja/kirjanpitaja.service'
import { asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { AreYouSureDialog, AreYouSureDialogData } from '../../../_jaettu-angular/_components/are-you-sure.dialog'
import { KirjanpitajanNimitiedot } from '../../../_jaettu-lemonator/model/kirjanpitaja'
import { LlmLuottokorttilaskuPromptRow } from '../../../_jaettu-lemonator/model/llm-luottokorttilasku'
import { callLlmLuottokorttilasku } from '../llm-luottokorttilasku.client'
import { luoPromptiversioVertailu, PromptiversioVertailu, VertailtavaPromptiversio } from '../prompti-versiovertailu.component'
import { LuoLuottokorttilaskuPromptDialog, LuoLuottokorttilaskuPromptDialogResult } from './luo-luottokorttilasku-prompt.dialog'

interface PromptiLomake {
  nimi: string
  sisalto: string
}

@Component({
  selector: 'app-luottokorttilasku-prompt',
  templateUrl: './luottokorttilasku-prompt.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LuottokorttilaskuPromptComponent implements OnInit {
  readonly promptit = signal<LlmLuottokorttilaskuPromptRow[]>([])
  readonly promptitLataa = signal(false)

  valittuPromptiId: string | null = null
  promptiValinta = form(signal<string | null>(null))
  readonly valittuPrompti = signal<LlmLuottokorttilaskuPromptRow | null>(null)

  lomake = form(signal<PromptiLomake>({ nimi: '', sisalto: '' }), polku => {
    maxLength(polku.nimi, 120)
  })

  readonly tallennetaan = signal(false)
  readonly poistetaan = signal(false)

  readonly historia = signal<LlmLuottokorttilaskuPromptRow[]>([])
  readonly historiaLataa = signal(false)
  readonly naytaHistoria = signal(false)
  esikatselu: LlmLuottokorttilaskuPromptRow | null = null
  readonly esikatseluVertailu = signal<PromptiversioVertailu | null>(null)

  readonly jsonKontrakti = signal('')
  readonly naytaKokoPrompt = signal(false)

  private readonly _kirjanpitajatMap = signal<Map<string, KirjanpitajanNimitiedot>>(new Map())

  constructor(
    private _firebase: FirebaseLemonator,
    private _snackbar: MatSnackBar,
    private _errorHandler: ErrorHandler,
    private _dialog: MatDialog,
    private _kirjanpitajaService: KirjanpitajaService,
  ) {}

  async ngOnInit() {
    const kirjanpitajatPromise = firstValueFrom(this._kirjanpitajaService.kirjanpitajienNimitiedotMapObservable).then(map => {
      this._kirjanpitajatMap.set(map)
    })
    await Promise.all([this._lataaPromptit(), kirjanpitajatPromise])
    if (this.promptit().length > 0) {
      this.valitsePrompti(this.promptit()[0].promptiId)
    }
  }

  annaPaivittajanNimi(uid: string | null | undefined): string {
    if (!uid) {
      return '—'
    }
    const k = this._kirjanpitajatMap().get(uid)
    return k ? `${k.etunimi} ${k.sukunimi}` : uid
  }

  vertaaPromptit = (a: string, b: string): boolean => a === b

  async valitsePrompti(promptiId: string) {
    this.naytaHistoria.set(false)
    this.suljeEsikatselu()
    this.historia.set([])
    this.valittuPromptiId = promptiId
    asetaArvo(this.promptiValinta, promptiId)
    const rivi = this.promptit().find(p => p.promptiId === promptiId) ?? null
    this.valittuPrompti.set(rivi)
    asetaArvo(this.lomake.nimi, rivi?.nimi ?? '')
    asetaArvo(this.lomake.sisalto, rivi?.sisalto ?? '')
  }

  avaaLuontiDialog() {
    const ref = this._dialog.open<LuoLuottokorttilaskuPromptDialog, void, LuoLuottokorttilaskuPromptDialogResult | undefined>(LuoLuottokorttilaskuPromptDialog, { autoFocus: false })
    ref.afterClosed().subscribe(async tulos => {
      if (!tulos?.promptiId) {
        return
      }
      await this._lataaPromptit()
      this.valitsePrompti(tulos.promptiId)
    })
  }

  async tallenna() {
    if (this.tallennetaan() || !this.valittuPromptiId) {
      return
    }
    const nimi = this.lomake.nimi().value().trim()
    const sisalto = this.lomake.sisalto().value()
    if (!nimi || !sisalto.trim()) {
      this._snackbar.open('Nimi ja sisältö ovat pakollisia', 'OK', { duration: 5000 })
      return
    }
    this.tallennetaan.set(true)
    try {
      const vastaus = await callLlmLuottokorttilasku(this._firebase, 'tallennaPromptUusiVersio', {
        promptiId: this.valittuPromptiId,
        nimi,
        sisalto,
      })
      if (vastaus.e || vastaus.versio == null) {
        this._snackbar.open('Tallennus epäonnistui: ' + (vastaus.e ?? 'tuntematon virhe'), 'OK', { duration: 8000 })
        return
      }
      await this._lataaPromptit()
      this.valitsePrompti(this.valittuPromptiId)
      this._snackbar.open(`Uusi versio v${vastaus.versio} tallennettu`, 'OK', { duration: 5000 })
    } catch (err) {
      this._errorHandler.handleError(err)
      this._snackbar.open('Tallennus epäonnistui', 'OK', { duration: 8000 })
    } finally {
      this.tallennetaan.set(false)
    }
  }

  async poistaVahvistuksella() {
    if (!this.valittuPromptiId || !this.valittuPrompti() || this.poistetaan()) {
      return
    }
    const dialogData: AreYouSureDialogData = {
      header: 'Poista prompti',
      text: `Poista prompti "${this.valittuPrompti().nimi}"? Tätä ei voi perua UI:sta.`,
      rightAction: 'Poista',
    }
    const yes = await firstValueFrom(this._dialog.open(AreYouSureDialog, { data: dialogData }).afterClosed())
    if (!yes) {
      return
    }
    this.poistetaan.set(true)
    try {
      const vastaus = await callLlmLuottokorttilasku(this._firebase, 'poistaPrompt', { promptiId: this.valittuPromptiId })
      if (vastaus.e) {
        this._snackbar.open('Poisto epäonnistui: ' + vastaus.e, 'OK', { duration: 8000 })
        return
      }
      await this._lataaPromptit()
      if (this.promptit().length > 0) {
        this.valitsePrompti(this.promptit()[0].promptiId)
      } else {
        this.valittuPromptiId = null
        asetaArvo(this.promptiValinta, null)
        this.valittuPrompti.set(null)
        asetaArvo(this.lomake.nimi, '')
        asetaArvo(this.lomake.sisalto, '')
      }
      this._snackbar.open('Prompti poistettu', 'OK', { duration: 5000 })
    } catch (err) {
      this._errorHandler.handleError(err)
      this._snackbar.open('Poisto epäonnistui', 'OK', { duration: 8000 })
    } finally {
      this.poistetaan.set(false)
    }
  }

  async toggleHistoria() {
    if (!this.valittuPromptiId) {
      return
    }
    this.naytaHistoria.set(!this.naytaHistoria())
    if (this.naytaHistoria() && this.historia().length === 0) {
      await this._lataaHistoria(this.valittuPromptiId)
    }
  }

  esikatsele(rivi: LlmLuottokorttilaskuPromptRow) {
    this.esikatselu = rivi
    this._paivitaVertailu()
  }

  suljeEsikatselu() {
    this.esikatselu = null
    this._paivitaVertailu()
  }

  toggleNaytaKokoPrompt() {
    this.naytaKokoPrompt.set(!this.naytaKokoPrompt())
  }

  get kokoSystemPrompt(): string {
    return `${this.jsonKontrakti()}\n\n${this.lomake.sisalto().value()}`
  }

  async palautaVersio(versio: number) {
    if (!this.valittuPromptiId || this.tallennetaan()) {
      return
    }
    const dialogData: AreYouSureDialogData = {
      header: 'Palauta versio',
      text: `Palauta versio v${versio} uudeksi versioksi?`,
      rightAction: 'Palauta',
    }
    const yes = await firstValueFrom(this._dialog.open(AreYouSureDialog, { data: dialogData }).afterClosed())
    if (!yes) {
      return
    }
    this.tallennetaan.set(true)
    try {
      const vastaus = await callLlmLuottokorttilasku(this._firebase, 'palautaPromptVersio', {
        promptiId: this.valittuPromptiId,
        versio,
      })
      if (vastaus.e || vastaus.versio == null) {
        this._snackbar.open('Palautus epäonnistui: ' + (vastaus.e ?? 'tuntematon virhe'), 'OK', { duration: 8000 })
        return
      }
      await this._lataaPromptit()
      this.valitsePrompti(this.valittuPromptiId)
      await this._lataaHistoria(this.valittuPromptiId)
      this.suljeEsikatselu()
      this._snackbar.open(`Versio v${vastaus.versio} luotu vanhalla sisällöllä`, 'OK', { duration: 5000 })
    } catch (err) {
      this._errorHandler.handleError(err)
      this._snackbar.open('Palautus epäonnistui', 'OK', { duration: 8000 })
    } finally {
      this.tallennetaan.set(false)
    }
  }

  private async _lataaPromptit() {
    this.promptitLataa.set(true)
    try {
      const vastaus = await callLlmLuottokorttilasku(this._firebase, 'haePromptit', {})
      if (vastaus.e) {
        this._snackbar.open('Promptien haku epäonnistui', 'OK', { duration: 8000 })
        return
      }
      this.promptit.set(vastaus.rivit)
      if (vastaus.jsonKontrakti) {
        this.jsonKontrakti.set(vastaus.jsonKontrakti)
      }
    } catch (err) {
      this._errorHandler.handleError(err)
    } finally {
      this.promptitLataa.set(false)
    }
  }

  private async _lataaHistoria(promptiId: string) {
    this.historiaLataa.set(true)
    try {
      const vastaus = await callLlmLuottokorttilasku(this._firebase, 'haePromptHistoria', { promptiId })
      if (vastaus.e) {
        this._snackbar.open('Historian haku epäonnistui', 'OK', { duration: 8000 })
        return
      }
      this.historia.set(vastaus.rivit)
      this._paivitaVertailu()
    } catch (err) {
      this._errorHandler.handleError(err)
    } finally {
      this.historiaLataa.set(false)
    }
  }

  private _paivitaVertailu() {
    const muunna = (rivi: LlmLuottokorttilaskuPromptRow): VertailtavaPromptiversio => ({ versio: rivi.versio, paivitetty: rivi.paivitetty, teksti: rivi.sisalto, nimi: rivi.nimi })
    this.esikatseluVertailu.set(this.esikatselu ? luoPromptiversioVertailu(this.esikatselu, this.historia(), this.valittuPrompti(), muunna) : null)
  }
}
